import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

import {
  OperatorTheme,
  OperatorThemeToken
} from '../../models/partner-operation';
import { ConfigService } from '../config/config.service';

@Injectable({
  providedIn: 'root'
})
export class OperatorThemeService {

  // Styling only (colors, typography, spacing, radius). Sibling registries,
  // each its own service, managed by DME: OperatorConfigService
  // (iframe/operator_config.json - currency etc.) and
  // OperatorTranslationService (iframe/operator_translations.json) -
  // nothing about currency or translations belongs here.
  //
  // One file per operator - deliberately NOT a single shared
  // operator_styles.json registry (retired): each operator's theme is its
  // own versioned artifact, published independently, which is what a
  // self-serve submission/approval flow needs (see the operator
  // integration guide) - publishing one operator's theme can never touch
  // another's file, and there's nothing here to merge/conflict on.
  //
  // Served from the image gateway bucket (ConfigService.PROXIES.image_gateway),
  // under an "iframe" folder shared with pmu-mobile - both apps fetch the
  // exact same remote operator files rather than keeping independent local
  // copies.
  private readonly operatorsPath = 'iframe/operators';

  /**
   * Explicit whitelist of the CSS custom properties an operator theme may
   * ever set. Never blindly apply every property coming from the JSON.
   */
  private readonly allowedProperties: Array<keyof OperatorTheme> = [
    '--theme-primary',
    '--theme-primary-light',
    '--theme-secondary',
    '--theme-cta-success',
    '--theme-primary-text',
    '--theme-secondary-text',
    '--theme-cta-success-text',
    '--reunion-tab-active-bg',
    '--reunion-tab-active-text',
    '--reunion-tab-active-border',
    '--bet-type-ring',
    '--race-date-nav-bg',
    '--race-date-nav-text',
    '--race-date-nav-border',
    '--partant-formula-idle-bg',
    '--horse-number-badge-bg',
    '--horse-number-badge-text',
    '--betslip-spot-badge-bg',
    '--betslip-spot-badge-text',
    '--rapport-position-badge-bg',
    '--rapport-position-badge-text',
    '--rapport-tied-badge-bg',
    '--rapport-tied-badge-text',
    '--race-status-ended-bg',
    '--race-status-ended-text',
    '--row-stripe-odd-bg',
    '--row-stripe-even-bg',
    '--row-warning-bg',
    '--operator-font-scale',
    '--operator-line-height',
    '--operator-spacing-density',
    '--operator-radius-scale'
  ];

  /**
   * Numeric-scale properties (unitless multipliers) get a range check
   * instead of the hex-color check every other allowed property gets.
   * Ranges mirror design-system/schema/operator-theme.schema.json.
   */
  private readonly numericPropertyRanges: Partial<Record<keyof OperatorTheme, [number, number]>> = {
    '--operator-font-scale': [0.8, 1.2],
    '--operator-line-height': [1.2, 2.0],
    '--operator-spacing-density': [0.8, 1.2],
    '--operator-radius-scale': [0, 2]
  };

  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
  }

  /**
   * Fetch and apply the theme file for one operator.
   *
   * A missing file (no theme published for this operator, or an unknown
   * operator id) is a normal case, not an error - it resolves to `false`
   * and leaves the platform default in place.
   */
  applyOperatorTheme(operatorName: string): Observable<boolean> {

    const normalizedOperator =
      this.normalizeOperatorName(operatorName);

    if (!normalizedOperator) {
      this.clearOperatorTheme();
      return of(false);
    }

    // Waits for ConfigService.configsLoad$ - PROXIES.image_gateway isn't
    // populated until loadAppConfigs() resolves.
    return this.configService.configsLoad$.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this.http.get<OperatorThemeToken>(
        `${this.configService.PROXIES.image_gateway}${this.operatorsPath}/${normalizedOperator}.json`
      )),
      map(token => this.tokenToRuntimeTheme(token)),

      catchError(() => of(null)),

      tap(theme => {
        // Important if operator changed in same SPA session.
        this.clearOperatorTheme();

        if (theme) {
          this.applyTheme(theme);
        }
      }),

      map(theme => !!theme)
    );
  }

  /**
   * Remove runtime overrides.
   *
   * The original SCSS values immediately become active again.
   */
  clearOperatorTheme(): void {

    const root = this.document.documentElement;

    this.allowedProperties.forEach(property => {
      root.style.removeProperty(property);
    });
  }

  /**
   * Maps a fetched operator theme token file (schemaVersion "2.0") to the
   * flat OperatorTheme CSS-custom-property shape applyTheme() knows how to
   * apply. Only ever sets a key the operator actually supplied (or, for
   * primaryLight, has a color to fall back to).
   */
  private tokenToRuntimeTheme(token: OperatorThemeToken): OperatorTheme {

    const brand = token?.color?.brand;
    const cta = token?.color?.cta;
    const component = token?.color?.component || {};

    if (!brand || !cta) {
      return {};
    }

    const theme: OperatorTheme = {
      '--theme-primary': brand.primary,
      '--theme-primary-light': brand.primaryLight || brand.primary,
      '--theme-primary-text': brand.primaryText,
      '--theme-secondary': brand.secondary,
      '--theme-secondary-text': brand.secondaryText,
      '--theme-cta-success': cta.success,
      '--theme-cta-success-text': cta.successText
    };

    if (component.reunionTab) {
      const t = component.reunionTab;
      if (t.activeBg) theme['--reunion-tab-active-bg'] = t.activeBg;
      if (t.activeText) theme['--reunion-tab-active-text'] = t.activeText;
      if (t.activeBorder) theme['--reunion-tab-active-border'] = t.activeBorder;
    }

    if (component.raceDateNav) {
      const n = component.raceDateNav;
      if (n.bg) theme['--race-date-nav-bg'] = n.bg;
      if (n.text) theme['--race-date-nav-text'] = n.text;
      if (n.border) theme['--race-date-nav-border'] = n.border;
    }

    if (component.betType?.ring) {
      theme['--bet-type-ring'] = component.betType.ring;
    }

    if (component.partantFormula?.idleBg) {
      theme['--partant-formula-idle-bg'] = component.partantFormula.idleBg;
    }

    if (component.horseNumberBadge) {
      const h = component.horseNumberBadge;
      if (h.bg) theme['--horse-number-badge-bg'] = h.bg;
      if (h.text) theme['--horse-number-badge-text'] = h.text;
    }

    if (component.betslipSpotBadge) {
      const b = component.betslipSpotBadge;
      if (b.bg) theme['--betslip-spot-badge-bg'] = b.bg;
      if (b.text) theme['--betslip-spot-badge-text'] = b.text;
    }

    if (component.rapportPositionBadge) {
      const p = component.rapportPositionBadge;
      if (p.bg) theme['--rapport-position-badge-bg'] = p.bg;
      if (p.text) theme['--rapport-position-badge-text'] = p.text;
    }

    if (component.rapportTiedBadge) {
      const t2 = component.rapportTiedBadge;
      if (t2.bg) theme['--rapport-tied-badge-bg'] = t2.bg;
      if (t2.text) theme['--rapport-tied-badge-text'] = t2.text;
    }

    if (component.raceStatusEnded) {
      const r = component.raceStatusEnded;
      if (r.bg) theme['--race-status-ended-bg'] = r.bg;
      if (r.text) theme['--race-status-ended-text'] = r.text;
    }

    if (component.rowStriping) {
      const s = component.rowStriping;
      if (s.oddBg) theme['--row-stripe-odd-bg'] = s.oddBg;
      if (s.evenBg) theme['--row-stripe-even-bg'] = s.evenBg;
    }

    if (component.rowNonPartant?.bg) {
      theme['--row-warning-bg'] = component.rowNonPartant.bg;
    }

    if (token.typography?.scale !== undefined) {
      theme['--operator-font-scale'] = String(token.typography.scale);
    }
    if (token.typography?.bodyLineHeight !== undefined) {
      theme['--operator-line-height'] = String(token.typography.bodyLineHeight);
    }
    if (token.spacing?.density !== undefined) {
      theme['--operator-spacing-density'] = String(token.spacing.density);
    }
    if (token.radius?.scale !== undefined) {
      theme['--operator-radius-scale'] = String(token.radius.scale);
    }

    return theme;
  }

  private applyTheme(theme: OperatorTheme): void {

    const root = this.document.documentElement;

    this.allowedProperties.forEach(property => {

      const value = theme[property];

      if (!value) {
        return;
      }

      const range = this.numericPropertyRanges[property];

      const isValid = range
        ? this.isValidNumericScale(value, range[0], range[1])
        : this.isValidColor(value);

      if (!isValid) {
        console.warn(
          `[OperatorTheme] Invalid value for ${property}:`,
          value
        );

        return;
      }

      root.style.setProperty(
        property,
        value
      );
    });
  }

  private normalizeOperatorName(operatorName: string): string {

    return (operatorName || '')
      .trim()
      .toLowerCase();
  }

  /**
   * Currently your configuration uses HEX colors.
   *
   * Keep this deliberately restrictive.
   */
  private isValidColor(value: string): boolean {

    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/
      .test(value.trim());
  }

  /**
   * Numeric-scale properties are unitless multipliers (e.g. "1.05"),
   * never raw CSS lengths - this is what keeps an operator from being
   * able to inject an arbitrary calc()/unit through this path.
   */
  private isValidNumericScale(value: string, min: number, max: number): boolean {

    if (!/^-?\d+(\.\d+)?$/.test(value.trim())) {
      return false;
    }

    const parsed = Number(value);

    return parsed >= min && parsed <= max;
  }
}
